<template>
    <div class="tw:inline-block" :class="{ 'is-not-clickable': disabled }" v-bind="$attrs">
        <div
            ref="triggerContainer"
            class="tw:inline-block tw:w-full"
            :class="{ 'tw:cursor-pointer': !disabled }"
            aria-haspopup="true"
            :aria-expanded="visible"
            @click.capture="togglePopover"
        >
            <slot name="trigger" :active="visible" />
        </div>
        <Popover ref="popover" :append-to="appendToBody ? 'body' : undefined" @hide="onHide" @show="onShow">
            <div
                class="e-dropdown-content"
                :style="scrollable ? { maxHeight: `${maxHeight}px`, overflowY: 'auto' } : {}"
            >
                <slot />
            </div>
        </Popover>
    </div>
</template>

<script>
import Popover from 'primevue/popover';
import { computed, provide, ref } from 'vue';
/**
 * EDropdown - Abstraction wrapper recreating the Buefy b-dropdown experience
 *
 * Usage:
 *   <e-dropdown>
 *     <template #trigger>
 *       <e-button>Click me</e-button>
 *     </template>
 *     <e-dropdown-item>Option 1</e-dropdown-item>
 *     <e-dropdown-item>Option 2</e-dropdown-item>
 *     <e-dropdown-item>Option 3</e-dropdown-item>
 *   </e-dropdown>
 *
 *   <e-dropdown aria-role="list">
 *     <template #trigger>
 *       <e-button icon-right="menu-down">
 *         Actions
 *       </e-button>
 *     </template>
 *     <e-dropdown-item aria-role="listitem">Edit</e-dropdown-item>
 *     <e-dropdown-item aria-role="listitem">Delete</e-dropdown-item>
 *   </e-dropdown>
 *
 *   <e-dropdown position="is-bottom-left" hoverable>
 *     <template #trigger>
 *       <e-icon icon="ellipsis-h" />
 *     </template>
 *     <e-dropdown-item>Settings</e-dropdown-item>
 *     <e-dropdown-item separator />
 *     <e-dropdown-item>Logout</e-dropdown-item>
 *   </e-dropdown>
 *
 * All Buefy b-dropdown props, events, and slots are supported.
 *
 * To access the underlying b-dropdown component methods, use the ref:
 *   <e-dropdown ref="myDropdown" />
 *   this.$refs.myDropdown.toggle()
 */
export default {
    name: 'EDropdown',
    components: {
        Popover,
    },
    inheritAttrs: false,
    props: {
        modelValue: {
            type: [String, Number, Boolean, Object, Array],
            default: null,
        },
        multiple: {
            type: Boolean,
            default: false,
        },
        appendToBody: {
            type: Boolean,
            default: false,
        },
        scrollable: {
            type: Boolean,
            default: false,
        },
        maxHeight: {
            type: [String, Number],
            default: 200,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['update:modelValue', 'active-change', 'change'],
    setup(props, { emit }) {
        const popover = ref(null);
        const dropdown = ref(null);
        const triggerContainer = ref(null);
        const visible = ref(false);

        const togglePopover = event => {
            if (props.disabled) return;

            if (popover.value) {
                const target = event?.currentTarget || event?.target || triggerContainer.value;
                if (visible.value) {
                    popover.value.hide();
                } else {
                    popover.value.show(event, target);
                }
            } else if (dropdown.value) {
                dropdown.value.toggle();
            }
        };

        const close = () => {
            if (popover.value) {
                popover.value.hide();
                visible.value = false;
            } else if (dropdown.value) {
                dropdown.value.isActive = false;
            }
        };

        const selectItem = value => {
            if (props.multiple) {
                const newValue = Array.isArray(props.modelValue) ? [...props.modelValue] : [];
                const index = newValue.findIndex(item => JSON.stringify(item) === JSON.stringify(value));
                if (index > -1) {
                    newValue.splice(index, 1);
                } else {
                    newValue.push(value);
                }
                emit('update:modelValue', newValue);
                emit('change', newValue);
            } else {
                emit('update:modelValue', value);
                emit('change', value);
                close();
            }
        };

        // Provide close method and selectItem to children (EDropdownItem)
        provide('closeDropdown', close);
        provide('selectItem', selectItem);
        provide(
            'modelValue',
            computed(() => props.modelValue)
        );
        provide(
            'multiple',
            computed(() => props.multiple)
        );

        const onActiveChange = active => {
            emit('active-change', active);
        };

        const onShow = () => {
            visible.value = true;
            emit('active-change', true);
        };

        const onHide = () => {
            visible.value = false;
            emit('active-change', false);
        };

        const show = event => {
            if (popover.value) {
                const target = event?.currentTarget || event?.target || triggerContainer.value;
                // If no event is provided (programmatic call), create a minimal event object
                const eventObject = event || { currentTarget: target };
                popover.value.show(eventObject, target);
            } else if (dropdown.value) {
                dropdown.value.isActive = true;
            }
        };

        const hide = () => {
            if (popover.value) {
                popover.value.hide();
            } else if (dropdown.value) {
                dropdown.value.isActive = false;
            }
        };

        const toggle = event => {
            togglePopover(event);
        };

        return {
            popover,
            dropdown,
            triggerContainer,
            togglePopover,
            visible,
            onActiveChange,
            onShow,
            onHide,
            show,
            hide,
            toggle,
        };
    },
    computed: {
        $el() {
            return this.$refs.dropdown?.$el;
        },
    },
};
</script>
